import React from "react";
import { X, ArrowUpRight, MessageSquare, Star, ExternalLink, MessageCircle } from "lucide-react";
import type { DataRecord, SocialQueueItem } from "../../types";
import { stripGoogleTranslation, formatDateShort } from "./socialFormat";
import { getClientBrandMeta } from "./socialBrand";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  item: DataRecord | SocialQueueItem | null;
  clientSlug: string;
  onOpenReplyModal?: (queueItem: SocialQueueItem) => void;
  getDirectLink?: (row: any) => string;
}

export function SocialCommentDetailModal({
  isOpen,
  onClose,
  item,
  clientSlug,
  onOpenReplyModal,
  getDirectLink,
}: Props) {
  if (!isOpen || !item) return null;

  const brandMeta = getClientBrandMeta(clientSlug);
  const text = (v: any, fallback = "") => (v !== null && v !== undefined ? String(v) : fallback);
  const number = (v: any, fallback = 0) => {
    const n = Number(v);
    return isNaN(n) ? fallback : n;
  };

  const platform = text(item.platform, "gbp").toLowerCase();
  const isGbp = platform === "gbp" || platform.includes("google");
  const isIg = platform === "meta_ig" || platform.includes("ig") || platform.includes("instagram");
  const isFb = platform === "meta_fb" || platform.includes("fb") || platform.includes("facebook");

  const platformColor = isGbp ? "#34a853" : isIg ? "#e1306c" : isFb ? "#1877f2" : "#7d96ff";
  const platformName = isGbp ? "Google BP" : isIg ? "Instagram" : isFb ? "Facebook" : "Canal social";

  const isPaid = text(item.origin, "").toLowerCase() === "paid";
  const authorName = text(item.author_name, "Usuario / Paciente");
  const starRating = item.star_rating !== null && item.star_rating !== undefined ? number(item.star_rating) : null;
  const rawMessage = text(item.message);
  const cleanedMessage = stripGoogleTranslation(rawMessage);
  const sedeName = text((item as any).post_title) || text((item as any).page_id) || "Canal oficial";

  const directLink = getDirectLink ? getDirectLink(item) : "#";

  const handleReplyInDashboard = () => {
    if (!onOpenReplyModal) return;
    const queueItem: SocialQueueItem = {
      feedback_id: text((item as any).feedback_id),
      date: text((item as any).date),
      created_time: text((item as any).created_time || (item as any).date),
      platform: platform,
      origin: text((item as any).origin, "organic"),
      author_name: authorName,
      message: rawMessage,
      star_rating: starRating,
      has_reply: Boolean(item.has_reply),
      post_id: text((item as any).post_id),
      ad_account_id: (item as any).ad_account_id || null,
      page_id: text((item as any).page_id),
      action_link: (item as any).action_link || null,
      polaridad: (item as any).polaridad || "negativa",
      acto_de_habla: (item as any).acto_de_habla || "reclamo",
      aspecto: (item as any).aspecto || "servicio",
      plan: (item as any).plan || {
        action: "reply",
        priority: 1,
        reason: "Comentario para revisión",
        template_key: "standard",
        draft_text: `Hola ${authorName !== "Usuario / Paciente" && authorName !== "Anónimo" ? authorName : ""}. Muchas gracias por comunicarte con ${brandMeta.displayName}. Queremos revisar tu caso y asistirte de inmediato. ¿Podrías enviarnos un mensaje privado con tus datos para coordinar con nuestro equipo?`,
      },
    };
    onClose();
    onOpenReplyModal(queueItem);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.65)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "16px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "var(--surface, #141822)",
          border: "1px solid var(--line, rgba(255, 255, 255, 0.12))",
          borderRadius: "16px",
          width: "100%",
          maxWidth: "580px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 60px rgba(0, 0, 0, 0.45)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            padding: "18px 20px",
            borderBottom: "1px solid var(--line, rgba(255, 255, 255, 0.08))",
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "6px" }}>
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: 750,
                  padding: "3px 8px",
                  borderRadius: "6px",
                  background: isGbp
                    ? "rgba(52, 168, 83, 0.15)"
                    : isIg
                    ? "rgba(225, 48, 108, 0.15)"
                    : "rgba(24, 119, 242, 0.15)",
                  color: platformColor,
                }}
              >
                {platformName}
              </span>

              <span
                style={{
                  fontSize: "11px",
                  fontWeight: 650,
                  padding: "2px 7px",
                  borderRadius: "5px",
                  background: isPaid ? "rgba(255, 159, 67, 0.15)" : "var(--surface-2, rgba(255, 255, 255, 0.06))",
                  color: isPaid ? "var(--orange, #ff9f43)" : "var(--muted, #8e96a8)",
                }}
              >
                {isPaid ? "Anuncio (Paid)" : "Orgánico"}
              </span>

              {starRating !== null && (
                <span style={{ display: "inline-flex", gap: "2px", alignItems: "center" }}>
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      size={13}
                      fill={s <= starRating ? "#ff5c5c" : "transparent"}
                      color={s <= starRating ? "#ff5c5c" : "var(--dim, #5e6677)"}
                    />
                  ))}
                </span>
              )}
            </div>

            <div style={{ fontSize: "12px", color: "var(--muted, #8e96a8)" }}>
              {authorName} · {sedeName} · {formatDateShort(text((item as any).date))}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--muted, #8e96a8)",
              cursor: "pointer",
              padding: "4px",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body con texto completo */}
        <div
          style={{
            padding: "20px",
            overflowY: "auto",
            flex: 1,
          }}
        >
          <div
            style={{
              fontSize: "14px",
              lineHeight: 1.6,
              color: "var(--text, #f6f7fb)",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            "{cleanedMessage}"
          </div>
        </div>

        {/* Footer con opciones de respuesta */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
            padding: "16px 20px",
            borderTop: "1px solid var(--line, rgba(255, 255, 255, 0.08))",
            background: "var(--surface-2, rgba(255, 255, 255, 0.02))",
          }}
        >
          <a
            href={directLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: "12px",
              color: "var(--muted, #8e96a8)",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              padding: "6px 12px",
              borderRadius: "6px",
              border: "1px solid var(--line, rgba(255, 255, 255, 0.1))",
            }}
          >
            {isGbp ? "Ver en Google Maps" : isIg ? "Ver en Instagram" : isFb ? "Ver en Facebook" : "Ver publicación"}
            <ArrowUpRight size={13} />
          </a>

          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <button
              type="button"
              className="button button-ghost"
              onClick={onClose}
              style={{ fontSize: "12px", padding: "6px 14px" }}
            >
              Cerrar
            </button>
            <button
              type="button"
              className="button button-primary"
              onClick={handleReplyInDashboard}
              style={{
                fontSize: "12px",
                padding: "6px 16px",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: platformColor,
                borderColor: platformColor,
                color: "#ffffff",
                fontWeight: 650,
              }}
            >
              <MessageSquare size={14} />
              <span>Responder</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
